import type { UIMessage } from 'ai';
import { isToolUIPart, getToolName } from 'ai';
import { SearchActivitiesOutputSchema } from '@funkey/ai-tools/schemas';

/**
 * Inspect a single assistant `UIMessage` for a completed `search_activities`
 * tool result and return the result count.
 *
 * Returns `null` when:
 *  - the message is not an assistant turn
 *  - no `search_activities` tool part is present
 *  - the tool part has not produced an output yet (`state !== 'output-available'`)
 *  - the output fails schema validation
 *
 * Pure / framework-free so we can unit-test the analytics dispatch logic
 * without rendering a React tree.
 */
export function selectToolResult(
    message: UIMessage,
): { count: number | null; modelRanking: string | null } | null {
    if (message.role !== 'assistant') return null;

    for (let i = message.parts.length - 1; i >= 0; i--) {
        const part = message.parts[i];
        if (!part) continue;
        if (!isToolUIPart(part)) continue;
        if (getToolName(part) !== 'search_activities') continue;
        if (part.state !== 'output-available') continue;
        if (!('output' in part)) continue;

        const parsed = SearchActivitiesOutputSchema.safeParse(part.output);
        if (!parsed.success) return null;
        // `count` is absent on the "filters" path (the grid owns the total) —
        // surface it as null so callers don't treat a filter turn as 0 results.
        // `modelRanking` is absent whenever the listwise rerank didn't run
        // (filters path, explicit sort override, or an untraced caller).
        return { count: parsed.data.count ?? null, modelRanking: parsed.data.modelRanking ?? null };
    }

    return null;
}
